/**
 * Configuración del Mapa de Calor
 * 
 * Este archivo centraliza todas las configuraciones relacionadas
 * con el acceso a la aplicación externa del mapa de calor.
 */

/**
 * URL base de la aplicación del mapa de calor
 * Por defecto usa una variable de entorno, o un placeholder si no está configurada
 */
export const HEATMAP_APP_URL = process.env.REACT_APP_HEATMAP_URL || 'https://mapa-calor.example.com';

/**
 * Indica si el mapa de calor está configurado y disponible
 */
export const HEATMAP_ENABLED = process.env.REACT_APP_HEATMAP_ENABLED !== 'false';

/**
 * Configuración de cómo abrir el enlace del mapa de calor
 * - 'new-tab': Abre en una nueva pestaña (recomendado para aplicaciones externas)
 * - 'same-tab': Abre en la misma pestaña (sale del portal)
 */
export const HEATMAP_OPEN_MODE = 'new-tab';

/**
 * Permiso requerido para ver el acceso al mapa de calor
 * Este permiso debe configurarse en el backend y asignarse a los usuarios correspondientes
 */
export const HEATMAP_PERMISSION = 'VIEW_HEATMAP';

/**
 * Configuración visual del acceso al mapa de calor
 */
export const HEATMAP_CONFIG = {
  title: 'Mapa de Calor',
  subtitle: 'Visualización de rutas y recorridos',
  description: 'Accede al sistema de análisis y visualización de rutas en tiempo real',
  icon: 'FaMapMarkedAlt', // Nombre del ícono de react-icons
  color: '#e74c3c', // Color rojo/naranja que representa calor
  accentColor: '#c0392b', // Color de acento para efectos hover
};

/**
 * Función helper para construir la URL del mapa de calor
 * Permite agregar parámetros en el futuro (token, usuario, etc.)
 * 
 * @param {Object} params - Parámetros opcionales para la URL
 * @returns {string} - URL completa del mapa de calor
 * 
 * @example
 * const url = buildHeatmapUrl({ userId: '123', token: 'abc' });
 * // Retorna: https://mapa-calor.example.com?userId=123&token=abc
 */
export const buildHeatmapUrl = (params = {}) => {
  console.log('[HeatmapConfig] 🔧 buildHeatmapUrl llamado con params:', params);
  console.log('[HeatmapConfig] 🌐 HEATMAP_APP_URL:', HEATMAP_APP_URL);
  
  const url = new URL(HEATMAP_APP_URL);
  
  // Agregar parámetros de query si existen (permitir valores 0, false, etc.)
  Object.keys(params).forEach(key => {
    const value = params[key];
    // Solo excluir null, undefined y strings vacíos
    if (value !== null && value !== undefined && value !== '') {
      console.log(`[HeatmapConfig] ➕ Agregando parámetro: ${key} = ${typeof value === 'string' && value.length > 50 ? value.substring(0, 30) + '...' : value}`);
      url.searchParams.append(key, value);
    } else {
      console.log(`[HeatmapConfig] ⏭️ Omitiendo parámetro ${key} (valor: ${value})`);
    }
  });
  
  const finalUrl = url.toString();
  console.log('[HeatmapConfig] ✅ URL final construida:', finalUrl);
  console.log('[HeatmapConfig] 🔍 Query params en URL:', url.searchParams.toString());
  
  return finalUrl;
};

/**
 * Función para verificar si el mapa de calor está configurado correctamente
 * @returns {boolean}
 */
export const isHeatmapConfigured = () => {
  return HEATMAP_ENABLED && 
         HEATMAP_APP_URL && 
         !HEATMAP_APP_URL.includes('example.com');
};

/**
 * Función para abrir el mapa de calor
 * Maneja el comportamiento de abrir en nueva pestaña o misma pestaña
 * 
 * @param {Object} params - Parámetros opcionales para pasar a la URL
 */
export const openHeatmap = (params = {}) => {
  const url = buildHeatmapUrl(params);
  
  if (HEATMAP_OPEN_MODE === 'new-tab') {
    window.open(url, '_blank', 'noopener,noreferrer');
  } else {
    window.location.href = url;
  }
};

const heatmapConfig = {
  HEATMAP_APP_URL,
  HEATMAP_ENABLED,
  HEATMAP_OPEN_MODE,
  HEATMAP_PERMISSION,
  HEATMAP_CONFIG,
  buildHeatmapUrl,
  isHeatmapConfigured,
  openHeatmap,
};

export default heatmapConfig;
